import { useState, useRef, useEffect } from "react";
import {
  FaBars,
  FaBell,
} from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";

import {
  useLogoutMutation,
  useGetForgotPasswordUsersQuery,
} from "../slices/usersApiSlice";

import { logout } from "../slices/authSlice";

import logo from "../assets/logo.png";

const Header = () => {
  const { userInfo } = useSelector((state) => state.auth);

  const dispatch = useDispatch();

  const navigate = useNavigate();

  const [logoutApiCall] = useLogoutMutation();

  const [mobileOpen, setMobileOpen] = useState(false);

  const [userMenuOpen, setUserMenuOpen] = useState(false);

  const [roleMenuOpen, setRoleMenuOpen] = useState(false);

  const userMenuRef = useRef(null);

  const roleMenuRef = useRef(null);

  // ======================================================
  // PASSWORD RESET REQUESTS
  // ======================================================

  const {
    data: forgotPasswordData,
  } = useGetForgotPasswordUsersQuery(undefined, {
    skip: userInfo?.role !== "centerAdmin",
  });

  const forgotPasswordCount =
    forgotPasswordData?.users?.length || 0;

  // ======================================================
  // ROLE BASED NAVIGATION
  // ======================================================

  const roleMenus = {
    // ====================================================
    // PRODUCT ADMIN
    // ====================================================

    productAdmin: [
      {
        label: "Coaching Centers",
        path: "/coaching-centers",
      },

      {
        label: "User Management",
        path: "/admin/userlist",
      },

      {
        label: "Question Bank Subjects",
        path: "/question-bank-subjects",
      },

      {
        label: "Chapters",
        path: "/chapters",
      },

      {
        label: "Questions",
        path: "/questions/create",
      },
    ],

    // ====================================================
    // CENTER ADMIN
    // ====================================================

    centerAdmin: [
      {
        label: "User Management",
        path: "/admin/userlist",
      },

      {
        label: "Password Reset Requests",
        path: "/users/forgot-password-requests",
      },

      {
        label: "Quizzes",
        path: "/quizzes",
      },

      {
        label: "Quiz Reports",
        path: "/quiz-attempts/quiz",
      },
    ],

    // ====================================================
    // STUDENT
    // ====================================================

    student: [
      {
        label: "Available Quizzes",
        path: "/student/quizzes",
      },

      {
        label: "My Attempts",
        path: "/quiz-attempts/my",
      },
    ],
  };

  // ======================================================
  // LOGOUT
  // ======================================================

  const logoutHandler = async () => {
    try {
      await logoutApiCall().unwrap();
    } catch (error) {
      console.error(error);
    }

    setRoleMenuOpen(false);

    setUserMenuOpen(false);

    setMobileOpen(false);

    dispatch(logout());

    navigate("/");
  };

  // ======================================================
  // CLOSE MENUS WHEN CLICKING OUTSIDE
  // ======================================================

  useEffect(() => {
    const handler = (event) => {
      if (
        userMenuRef.current &&
        !userMenuRef.current.contains(event.target)
      ) {
        setUserMenuOpen(false);
      }

      if (
        roleMenuRef.current &&
        !roleMenuRef.current.contains(event.target)
      ) {
        setRoleMenuOpen(false);
      }
    };

    document.addEventListener("mousedown", handler);

    return () => {
      document.removeEventListener("mousedown", handler);
    };
  }, []);

  return (
    <>
      {/* ======================================================
          HEADER
      ====================================================== */}

      <header className="sticky top-0 z-50 bg-red-700 shadow-md">
        <div className="mx-auto flex h-16 items-center justify-between px-4">

          {/* ==================================================
              LEFT
          ================================================== */}

          <div className="flex items-center gap-3">

            {/* MOBILE MENU BUTTON */}

            <button
              type="button"
              className="text-white sm:hidden"
              onClick={() => setMobileOpen(true)}
            >
              <FaBars size={22} />
            </button>

            {/* LOGO */}

            <div
              className="flex cursor-pointer items-center gap-2"
              onClick={() => navigate("/home")}
            >
              <img
                src={logo}
                alt="logo"
                className="h-8"
              />

              <span className="text-xl font-bold text-white">
                Quiz Management System
              </span>
            </div>
          </div>

          {/* ==================================================
              DESKTOP MENU
          ================================================== */}

          <div className="hidden items-center gap-4 sm:flex">

            {/* HOME */}

            <button
              type="button"
              className="rounded px-3 py-2 text-white hover:bg-red-800"
              onClick={() => navigate("/home")}
            >
              Home
            </button>

            {/* ==================================================
                ROLE MENU
            ================================================== */}

            {userInfo?.role && (
              <div
                className="relative"
                ref={roleMenuRef}
              >
                <button
                  type="button"
                  className="rounded px-3 py-2 text-white hover:bg-red-800"
                  onClick={() =>
                    setRoleMenuOpen((prev) => !prev)
                  }
                >
                  {userInfo.role}
                </button>

                {roleMenuOpen && (
                  <div className="absolute right-0 mt-2 w-64 rounded bg-white py-2 shadow-lg">
                    {roleMenus[userInfo.role]?.map((item) => (
                      <button
                        type="button"
                        key={item.path}
                        className="block w-full px-4 py-2 text-left hover:bg-gray-100"
                        onClick={() => {
                          navigate(item.path);
                          setRoleMenuOpen(false);
                        }}
                      >
                        {item.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* ==================================================
                PASSWORD RESET NOTIFICATION
            ================================================== */}

            {userInfo?.role === "centerAdmin" && (
              <button
                type="button"
                onClick={() =>
                  navigate("/users/forgot-password-requests")
                }
                className="relative rounded-lg p-2 text-white hover:bg-red-800 transition"
                title="Password Reset Requests"
              >
                <FaBell size={20} />

                {forgotPasswordCount > 0 && (
                  <span
                    className="absolute -right-1 -top-1 flex h-5 min-w-5
                      items-center justify-center rounded-full
                      bg-yellow-400 px-1 text-xs font-bold text-red-800
                      ring-2 ring-red-700"
                  >
                    {forgotPasswordCount > 99
                      ? "99+"
                      : forgotPasswordCount}
                  </span>
                )}
              </button>
            )}

            {/* ==================================================
                USER PROFILE MENU
            ================================================== */}

            {userInfo ? (
              <div
                className="relative"
                ref={userMenuRef}
              >
                <button
                  type="button"
                  className="flex items-center gap-2 rounded px-2 py-1 text-white hover:bg-red-800"
                  onClick={() =>
                    setUserMenuOpen((prev) => !prev)
                  }
                >
                  <div className="flex h-8 w-8 items-center justify-center rounded-full bg-white font-semibold text-red-700">
                    {userInfo.name
                      ?.charAt(0)
                      .toUpperCase()}
                  </div>

                  <span>{userInfo.name}</span>

                  <span className="rounded bg-red-800 px-2 py-1 text-xs uppercase text-white">
                    {userInfo.role}
                  </span>
                </button>

                {userMenuOpen && (
                  <div className="absolute right-0 mt-2 w-48 rounded bg-white py-2 shadow-lg">
                    <button
                      type="button"
                      className="block w-full px-4 py-2 text-left hover:bg-gray-100"
                      onClick={logoutHandler}
                    >
                      Logout
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <button
                type="button"
                className="rounded px-3 py-2 text-white hover:bg-red-800"
                onClick={() => navigate("/")}
              >
                Sign In
              </button>
            )}
          </div>
        </div>
      </header>

      {/* ======================================================
          MOBILE DRAWER
      ====================================================== */}

      {mobileOpen && (
        <>
          {/* BACKDROP */}

          <div
            className="fixed inset-0 z-40 bg-black/40"
            onClick={() => setMobileOpen(false)}
          />

          {/* DRAWER */}

          <div className="fixed left-0 top-0 z-50 h-full w-64 bg-white shadow-lg">

            {/* DRAWER HEADER */}

            <div className="flex items-center justify-between border-b p-4">
              <span className="text-lg font-bold">
                Menu
              </span>

              <button
                type="button"
                onClick={() => setMobileOpen(false)}
                className="text-2xl"
              >
                ×
              </button>
            </div>

            <div className="flex flex-col">

              {/* ==================================================
                  HOME
              ================================================== */}

              <button
                type="button"
                className="border-b px-4 py-3 text-left hover:bg-gray-100"
                onClick={() => {
                  navigate("/home");
                  setMobileOpen(false);
                }}
              >
                Home
              </button>

              {/* ==================================================
                  PASSWORD RESET NOTIFICATIONS
              ================================================== */}

              {userInfo?.role === "centerAdmin" && (
                <button
                  type="button"
                  className="flex items-center justify-between border-b px-4 py-3 text-left hover:bg-gray-100"
                  onClick={() => {
                    navigate(
                      "/users/forgot-password-requests",
                    );

                    setMobileOpen(false);
                  }}
                >
                  <div className="flex items-center gap-3">
                    <FaBell className="text-red-600" />

                    <span>
                      Password Reset Requests
                    </span>
                  </div>

                  {forgotPasswordCount > 0 && (
                    <span
                      className="flex h-6 min-w-6 items-center justify-center
                        rounded-full bg-red-600 px-2
                        text-xs font-bold text-white"
                    >
                      {forgotPasswordCount > 99
                        ? "99+"
                        : forgotPasswordCount}
                    </span>
                  )}
                </button>
              )}

              {/* ==================================================
                  ROLE MENU
              ================================================== */}

              {userInfo?.role &&
                roleMenus[userInfo.role]?.map((item) => (
                  <button
                    type="button"
                    key={item.path}
                    className="border-b px-4 py-3 text-left hover:bg-gray-100"
                    onClick={() => {
                      navigate(item.path);
                      setMobileOpen(false);
                    }}
                  >
                    {item.label}
                  </button>
                ))}

              {/* ==================================================
                  LOGOUT / LOGIN
              ================================================== */}

              {userInfo ? (
                <button
                  type="button"
                  className="border-b px-4 py-3 text-left hover:bg-gray-100"
                  onClick={logoutHandler}
                >
                  Logout
                </button>
              ) : (
                <button
                  type="button"
                  className="border-b px-4 py-3 text-left hover:bg-gray-100"
                  onClick={() => {
                    navigate("/");
                    setMobileOpen(false);
                  }}
                >
                  Sign In
                </button>
              )}
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default Header;